// VentasStatsBar — las 6 pastillas de arriba de la tabla de Ventas (`.stats-bar` del mock v3):
// Todas · Hoy · Contado · Crédito · SureKeep · Externas. Además de contar, filtran.
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { StatsBar, type StatsBarItem, type StatsBarTone } from '@/shared/patterns/StatsBar';
import { SaleStatusEnum, SaleTypeEnum } from '@/domains/sales/wizard/enums';
import type {
    BackofficeSale,
    BackofficeSalesFilters,
    BackofficeSalesStats,
} from '@/api/services/retail-wizard/contract';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { dateShortcut } from '../lib/ventasFilters';

/**
 * Lo mínimo que necesitan las métricas. Se tipa como `Pick` y no como `BackofficeSale` para poder
 * calcular (y testear) sin construir la venta completa.
 */
export type SaleMetrics = Pick<
    BackofficeSale,
    'amountCents' | 'status' | 'type' | 'externalProvider'
> &
    Partial<Pick<BackofficeSale, 'soldCents' | 'equipmentPriceCents'>>;

/** Lo que además necesitan los contadores de los pills (el pill "Hoy" mira la fecha). */
export type SaleForPills = SaleMetrics & Pick<BackofficeSale, 'soldAt'>;

export interface VentasStats {
    total: number;
    volumenCents: number;
    ticketPromedioCents: number;
    pctSureKeep: number;
}

/**
 * El DTO de BO tipa `type` y `status` como `string` a propósito (el backend puede sumar valores
 * antes que el front y una fila no puede romperse por eso). Se comparan contra estas constantes
 * derivadas del enum: se conserva la fuente de verdad de `@fiado/type-kit` sin caer en la comparación
 * enum-vs-string que el lint prohíbe.
 */
const TIPO: Record<'CASH' | 'CREDIT' | 'EXTERNAL_REFERRAL', string> = {
    CASH: SaleTypeEnum.CASH,
    CREDIT: SaleTypeEnum.CREDIT,
    EXTERNAL_REFERRAL: SaleTypeEnum.EXTERNAL_REFERRAL,
};
const CANCELADA: string = SaleStatusEnum.CANCELLED;

/**
 * Una venta es EXTERNA si se derivó a una financiera de terceros.
 *
 * Se pregunta por el tipo `EXTERNAL_REFERRAL` **y** por un `externalProvider` distinto de SureKeep:
 * el conjunto abierto (cualquier proveedor no-SureKeep) evita que agregar una financiera nueva en
 * `ExternalProviderEnum` deje ventas sin clasificar hasta que alguien actualice esta lista.
 */
function esExterna(s: SaleMetrics): boolean {
    return (
        s.type === TIPO.EXTERNAL_REFERRAL ||
        // `!= null` (comparación laxa) y NO `!== null`: el DTO declara `string | null`, pero el silo
        // borra los nulos antes de escribir en DDB (`stripNulls`), así que una venta de contado llega
        // con el atributo AUSENTE, o sea `undefined`. Con `!== null`, `undefined` pasaba el filtro y
        // toda venta de contado se contaba como externa — verificado en Dev: 2 ventas CASH, el pill
        // "Externas" marcaba 2. La raíz está en `rowToSale` del lambda, que no normaliza este campo
        // con `?? null` como sí hace con los del snapshot.
        (s.externalProvider != null && s.externalProvider !== 'SUREKEEP')
    );
}

/**
 * Una venta es de SureKeep si está financiada por SureKeep y NO por un tercero.
 *
 * ⚠️ Ojo con la forma del dato: en el backend el crédito propio se guarda como
 * `type: CREDIT` con `externalProvider: null` — `SUREKEEP` **no** es un valor de
 * `ExternalProviderEnum` (solo PAYJOY, CREDIYA, LESPAGO, INNOVA). Preguntar solo por
 * `externalProvider === 'SUREKEEP'` daría 0% para siempre contra datos reales. Se aceptan las dos
 * formas —el tipo CREDIT y el proveedor literal— para que el porcentaje sea correcto hoy y siga
 * siéndolo si el backend algún día materializa SUREKEEP como proveedor.
 */
function esSureKeep(s: SaleMetrics): boolean {
    if (esExterna(s)) return false;
    return s.type === TIPO.CREDIT || s.externalProvider === 'SUREKEEP';
}

/**
 * Métricas sobre las ventas CARGADAS (no sobre todo el universo): con paginación por `nextToken` el
 * front solo conoce lo que trajo. La UI lo dice explícitamente —ver `stats.alcance` más abajo— para
 * no sugerir un total que no es.
 * Las canceladas se excluyen: inflarían el ticket promedio con dinero que se devolvió.
 */
export function computeStats(sales: SaleMetrics[]): VentasStats {
    const vivas = sales.filter((s) => s.status !== CANCELADA);
    // Volumen y ticket suman lo vendido de cada venta, igual que Tiendas y el Home.
    const volumenCents = vivas.reduce((acc, s) => acc + soldCentsOf(s), 0);
    const sk = vivas.filter(esSureKeep).length;
    return {
        total: vivas.length,
        volumenCents,
        ticketPromedioCents: vivas.length ? Math.round(volumenCents / vivas.length) : 0,
        pctSureKeep: vivas.length ? Math.round((sk / vivas.length) * 100) : 0,
    };
}

export type PillKey = 'todas' | 'hoy' | 'contado' | 'credito' | 'surekeep' | 'externas';

const PILL_ORDER: PillKey[] = ['todas', 'hoy', 'contado', 'credito', 'surekeep', 'externas'];

/** Llave de cada pill en el DTO del silo: «Todas» viaja como `total`, no como `todas`. */
const PILL_STAT_KEY: Record<PillKey, keyof BackofficeSalesStats> = {
    todas: 'total',
    hoy: 'hoy',
    contado: 'contado',
    credito: 'credito',
    surekeep: 'surekeep',
    externas: 'externas',
};

/**
 * Filtros que acotan la tabla y que `GET /backoffice/sales/stats` NO honra — solo mira `from`/`to`.
 * `type` y `provider` quedan fuera: son la dimensión que los pills mismos segmentan.
 */
const FILTERS_OUTSIDE_STATS = [
    'storeId',
    'sellerId',
    'customerId',
    'query',
    'status',
    'sku',
] as const;

/**
 * `true` cuando los conteos del silo describen el mismo conjunto que lista la tabla: con cualquier
 * filtro territorial o de búsqueda el endpoint sigue devolviendo el total de la cadena.
 */
export function serverStatsCoverFilters(f: BackofficeSalesFilters): boolean {
    if (FILTERS_OUTSIDE_STATS.some((k) => !!f[k])) return false;
    return f.minPesos === undefined && f.maxPesos === undefined;
}

/**
 * Filtro que aplica cada pill. Es un PARCHE sobre los filtros vigentes, no un reemplazo: elegir
 * "Contado" no debe borrar la búsqueda ni la tienda que el operador ya había puesto.
 *
 * `credito` y `surekeep` resuelven al MISMO filtro a propósito: hoy el crédito propio es
 * `SaleTypeEnum.CREDIT` y no hay una segunda financiera interna, así que son el mismo conjunto. El
 * backend filtra `type` por igualdad exacta (`SaleRepository.buildSaleQuery`), no acepta uniones, así
 * que "cualquier venta financiada" (CREDIT ∪ EXTERNAL_REFERRAL) no es expresable server-side. Se
 * mantienen los dos pills del mock; el día que SureKeep conviva con otra financiera interna, este
 * mapa es el único lugar a tocar.
 */
function pillPatch(pill: PillKey): Partial<BackofficeSalesFilters> {
    switch (pill) {
        case 'todas':
            // Limpia TAMBIÉN el rango de fechas. "Hoy" vive en esta misma tira, así que si "Todas" no
            // lo borrara no habría forma de deshacerlo desde los pills: quedaría filtrado por fecha
            // mostrando el rótulo "Todas", que es exactamente lo contrario de lo que dice.
            return { type: undefined, provider: undefined, from: undefined, to: undefined };
        case 'hoy':
            return dateShortcut(0);
        case 'contado':
            return { type: SaleTypeEnum.CASH, provider: undefined };
        case 'credito':
        case 'surekeep':
            return { type: SaleTypeEnum.CREDIT, provider: undefined };
        case 'externas':
            return { type: SaleTypeEnum.EXTERNAL_REFERRAL, provider: undefined };
    }
}

/** Un pill está "encendido" cuando su filtro es el que está aplicado, no cuando fue el último clic. */
function pillActivo(pill: PillKey, f: BackofficeSalesFilters): boolean {
    switch (pill) {
        case 'todas':
            // El rango de fechas cuenta: sin mirarlo, "Todas" seguía encendido junto a "Hoy" y la
            // tira mostraba dos pills activos a la vez, que se lee como que el filtro no se aplicó.
            return !f.type && !f.provider && !f.from && !f.to;
        case 'hoy': {
            const hoy = dateShortcut(0);
            return f.from === hoy.from && f.to === hoy.to;
        }
        case 'contado':
            return f.type === TIPO.CASH;
        case 'credito':
        case 'surekeep':
            return f.type === TIPO.CREDIT;
        case 'externas':
            return f.type === TIPO.EXTERNAL_REFERRAL;
    }
}

/**
 * Conteo del pill sobre las ventas CARGADAS. A diferencia de las métricas, aquí las canceladas SÍ
 * cuentan: el número anticipa cuántas filas verá el operador al hacer clic, y la tabla muestra las
 * canceladas. Un conteo que no cuadre con las filas se lee como bug.
 */
function pillCount(sales: SaleForPills[], pill: PillKey): number {
    switch (pill) {
        case 'todas':
            return sales.length;
        case 'hoy': {
            const hoy = dayjs().format('YYYY-MM-DD');
            return sales.filter((s) => dayjs(s.soldAt).format('YYYY-MM-DD') === hoy).length;
        }
        case 'contado':
            return sales.filter((s) => s.type === TIPO.CASH).length;
        case 'credito':
        case 'surekeep':
            return sales.filter(esSureKeep).length;
        case 'externas':
            return sales.filter(esExterna).length;
    }
}

/** Acento de color del mock: primary para "Hoy", success para SureKeep, info para externas. */
const PILL_TONE: Partial<Record<PillKey, StatsBarTone>> = {
    hoy: 'primary',
    surekeep: 'success',
    externas: 'info',
};

/** Las pastillas que cuenta el silo sin financiera resuelta: ahí cae el hueco de `sinProveedor`. */
const PILLS_WITH_GAP: readonly PillKey[] = ['todas', 'contado', 'credito'];

export interface VentasStatsBarProps {
    /** Ventas ya cargadas y filtradas por la búsqueda. */
    sales: BackofficeSale[];
    /** Los contadores del silo; sin ellos las pastillas cuentan lo cargado. */
    serverStats?: BackofficeSalesStats;
    filters: BackofficeSalesFilters;
    onChange: (next: BackofficeSalesFilters) => void;
    /** Sin lista que contar las pastillas informan «—» y no filtran. */
    available?: boolean;
    /** Ya no quedan páginas: lo cargado es el universo y cuadra con la tabla, canceladas incluidas. */
    allLoaded?: boolean;
    className?: string;
}

export function VentasStatsBar({
    sales,
    serverStats,
    filters,
    onChange,
    available = true,
    allLoaded = false,
    className,
}: VentasStatsBarProps) {
    const { t } = useTranslation('ventas');
    const label: Record<PillKey, string> = {
        todas: t('stats.todas', { defaultValue: 'Todas' }),
        hoy: t('stats.hoy', { defaultValue: 'Hoy' }),
        contado: t('stats.contado', { defaultValue: 'Contado' }),
        credito: t('stats.credito', { defaultValue: 'Crédito' }),
        surekeep: t('stats.surekeep', { defaultValue: 'SureKeep' }),
        externas: t('stats.externas', { defaultValue: 'Externas' }),
    };

    // El silo no cuenta canceladas: con todo cargado se cuenta lo listado, que cuadra con la tabla.
    const coveringStats = !allLoaded && serverStatsCoverFilters(filters) ? serverStats : undefined;
    const scopeNote = coveringStats
        ? t('stats.pillsSilo', { defaultValue: 'Conteos del periodo completo, sin las canceladas' })
        : allLoaded
          ? t('stats.pillsListadas', {
                n: sales.length,
                defaultValue: 'Conteos de las {{n}} ventas listadas, canceladas incluidas',
            })
          : t('stats.pillsCargadas', {
                n: sales.length,
                defaultValue:
                    'Conteos sobre las {{n}} ventas cargadas — el filtro activo no se cuenta en el servidor',
            });
    // Sin esta nota, «Contado + Crédito» no llega a «Todas» y nada explica el hueco.
    const gapNote = coveringStats?.sinProveedor
        ? t('stats.sinProveedor', {
              n: coveringStats.sinProveedor,
              defaultValue: '· {{n}} sin financiera resuelta, fuera de Contado y de Crédito',
          })
        : null;
    const unavailableNote = t('stats.sinLista', {
        defaultValue: 'Todavía no hay ventas que contar',
    });

    const items: StatsBarItem<PillKey>[] = PILL_ORDER.map((pill) => ({
        key: pill,
        label: label[pill],
        tone: PILL_TONE[pill],
        disabled: !available,
        value: !available
            ? '—'
            : coveringStats
              ? coveringStats[PILL_STAT_KEY[pill]]
              : pillCount(sales, pill),
        title: !available
            ? unavailableNote
            : gapNote !== null && PILLS_WITH_GAP.includes(pill)
              ? `${scopeNote} ${gapNote}`
              : scopeNote,
    }));

    return (
        <StatsBar
            items={items}
            active={PILL_ORDER.filter((pill) => pillActivo(pill, filters))}
            onChange={(pill) => {
                onChange({ ...filters, ...pillPatch(pill) });
            }}
            ariaLabel={t('stats.grupo', { defaultValue: 'Segmentos de ventas' })}
            className={className}
        />
    );
}
